import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import {
  Badge, EmptyState, PageLoading, Select, TextInput,
} from '../../components/ui';
import type { Area, DistributorArea, DistributorCustomer } from '../../types';

type Tab = 'overview' | 'areas' | 'retailers';

interface PriceListOption {
  id: string;
  name: string;
  code: string | null;
  isDefault: boolean;
  customerType: { id: string; name: string } | null;
}

interface DistributorDetail {
  id: string;
  fullName: string;
  email: string | null;
  mobile: string | null;
  status: string;
  designation: string | null;
  employeeCode: string | null;
  areaCount: number;
  retailerCount: number;
  primaryOrderCount: number;
  secondaryOrderCount: number;
  linkedCustomer: { id: string; name: string } | null;
  priceList: { id: string; name: string } | null;
  customerType: { id: string; name: string } | null;
}

export default function DistributorDetailDialog({
  distributorUserId, onClose, onChanged,
}: {
  distributorUserId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [tab, setTab] = useState<Tab>('overview');
  const [detail, setDetail] = useState<DistributorDetail | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await api.get<DistributorDetail>(`/distributors/${distributorUserId}`));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load distributor.');
    }
  }, [distributorUserId, toast]);

  useEffect(() => { void load(); }, [load]);

  if (!detail) {
    return (
      <Modal title="Loading…" onClose={onClose} wide>
        <PageLoading />
      </Modal>
    );
  }

  return (
    <Modal title={detail.fullName} subtitle={detail.designation ?? undefined} onClose={onClose} wide>
      <div className="col gap-16">
        {/* Tab bar — uses the app's .tabs / .tab classes */}
        <div className="tabs">
          {(['overview', 'areas', 'retailers'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              className={`tab${tab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'overview' ? 'Overview' : t === 'areas' ? 'Areas' : 'Retailers'}
            </button>
          ))}
        </div>

        {tab === 'overview' ? (
          <OverviewTab
            detail={detail}
            distributorUserId={distributorUserId}
            isAdmin={isAdmin}
            onPriceListChanged={load}
            onDetailChanged={() => { void load(); onChanged(); }}
          />
        ) : tab === 'areas' ? (
          <AreasTab
            distributorUserId={distributorUserId}
            isAdmin={isAdmin}
            onChanged={() => { void load(); onChanged(); }}
          />
        ) : (
          <RetailersTab
            distributorUserId={distributorUserId}
            isAdmin={isAdmin}
            onChanged={() => { void load(); onChanged(); }}
          />
        )}
      </div>
    </Modal>
  );
}

// =====================================================================
// Overview
// =====================================================================

function OverviewTab({ detail, distributorUserId, isAdmin, onPriceListChanged, onDetailChanged }: {
  detail: DistributorDetail;
  distributorUserId: string;
  isAdmin: boolean;
  onPriceListChanged: () => void;
  onDetailChanged: () => void;
}) {
  const toast = useToast();
  const [priceLists, setPriceLists] = useState<PriceListOption[] | null>(null);
  const [savingPL, setSavingPL] = useState(false);

  /* ── edit state ─────────────────────────────────────────────── */
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formMobile, setFormMobile] = useState('');
  const [formDesignation, setFormDesignation] = useState('');
  const [formEmployeeCode, setFormEmployeeCode] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    api.get<PriceListOption[]>('/orders/price-lists')
      .then((res) => setPriceLists(res))
      .catch(() => { /* non-critical */ });
  }, [isAdmin]);

  function startEditing() {
    setFormName(detail.fullName);
    setFormEmail(detail.email ?? '');
    setFormMobile(detail.mobile ?? '');
    setFormDesignation(detail.designation ?? '');
    setFormEmployeeCode(detail.employeeCode ?? '');
    setFormIsActive(detail.status === 'active');
    setEditing(true);
  }

  async function saveEdit() {
    if (!formName.trim()) {
      toast.error('Name is required.');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/distributors/${distributorUserId}`, {
        fullName: formName.trim(),
        email: formEmail.trim() || null,
        mobile: formMobile.trim() || null,
        designation: formDesignation.trim() || null,
        employeeCode: formEmployeeCode.trim() || null,
        isActive: formIsActive,
      });
      toast.success('Distributor updated.');
      setEditing(false);
      onDetailChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  }

  async function changePriceList(priceListId: string) {
    setSavingPL(true);
    try {
      await api.put(`/distributors/${distributorUserId}/price-list`, {
        priceListId: priceListId || null,
      });
      toast.success(priceListId ? 'Price list assigned.' : 'Price list cleared.');
      onPriceListChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update price list.');
    } finally {
      setSavingPL(false);
    }
  }

  return (
    <div className="col gap-20">
      {/* Status badge + employee code + edit button */}
      <div className="row gap-8" style={{ alignItems: 'center' }}>
        {!editing ? (
          <>
            <Badge tone={detail.status === 'active' ? 'success' : 'neutral'}>
              {detail.status === 'active' ? 'Active' : 'Inactive'}
            </Badge>
            {detail.employeeCode ? (
              <span className="mono small faint">#{detail.employeeCode}</span>
            ) : null}
            <div className="grow" />
            {isAdmin ? (
              <button type="button" className="btn btn-secondary btn-sm" onClick={startEditing}>
                <Icon name="edit" size={14} /> Edit
              </button>
            ) : null}
          </>
        ) : (
          <>
            <select
              className="select"
              value={formIsActive ? 'active' : 'inactive'}
              onChange={(e) => setFormIsActive(e.target.value === 'active')}
              style={{ width: 140, minHeight: 34 }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div className="grow" />
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => void saveEdit()}
              disabled={saving}
            >
              {saving ? <span className="spinner" /> : null}
              Save
            </button>
          </>
        )}
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: 10,
      }}>
        <StatCard icon="map" value={detail.areaCount} label="Areas" />
        <StatCard icon="users" value={detail.retailerCount} label="Retailers" />
        <StatCard icon="list" value={detail.primaryOrderCount} label="Primary orders" />
        <StatCard icon="list" value={detail.secondaryOrderCount} label="Secondary orders" />
      </div>

      {/* Contact */}
      <section>
        <h3 style={{ marginBottom: 10 }}>Contact</h3>
        {!editing ? (
          <div className="col gap-6">
            <InfoRow label="Email" value={detail.email} />
            <InfoRow label="Mobile" value={detail.mobile} />
            {detail.designation ? <InfoRow label="Title" value={detail.designation} /> : null}
          </div>
        ) : (
          <div className="col gap-12">
            <TextInput
              label="Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
            />
            <TextInput
              label="Email"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              type="email"
            />
            <TextInput
              label="Mobile"
              value={formMobile}
              onChange={(e) => setFormMobile(e.target.value)}
              type="tel"
            />
            <TextInput
              label="Title / Designation"
              value={formDesignation}
              onChange={(e) => setFormDesignation(e.target.value)}
            />
            <TextInput
              label="Employee code"
              value={formEmployeeCode}
              onChange={(e) => setFormEmployeeCode(e.target.value)}
            />
          </div>
        )}
      </section>

      {/* Pricing */}
      <section>
        <h3 style={{ marginBottom: 10 }}>Pricing</h3>
        <div className="col gap-6">
          {detail.customerType ? (
            <InfoRow label="Type" value={detail.customerType.name} />
          ) : null}

          <div className="row gap-12" style={{ alignItems: 'center' }}>
            <span className="small" style={{ color: 'var(--ink-faint)', minWidth: 90, fontWeight: 500 }}>Price list</span>
            {isAdmin && priceLists ? (
              <select
                className="select"
                value={detail.priceList?.id ?? ''}
                disabled={savingPL}
                onChange={(e) => void changePriceList(e.target.value)}
                style={{ flex: 1, maxWidth: 280, minHeight: 36, padding: '6px 32px 6px 10px', fontSize: '13.5px' }}
              >
                <option value="">Company default</option>
                {priceLists.map((pl) => (
                  <option key={pl.id} value={pl.id}>
                    {pl.name}{pl.customerType ? ` (${pl.customerType.name})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <span className="small">{detail.priceList?.name ?? 'Company default'}</span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="row gap-12" style={{ alignItems: 'baseline' }}>
      <span className="small" style={{ color: 'var(--ink-faint)', minWidth: 90, fontWeight: 500 }}>{label}</span>
      <span className="small" style={{ color: 'var(--ink)' }}>{value ?? '—'}</span>
    </div>
  );
}

function StatCard({ icon, value, label }: { icon: string; value: number; label: string }) {
  return (
    <div className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 'var(--r-sm)',
        background: 'var(--brand-soft)', color: 'var(--brand-dark)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon name={icon as any} size={18} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.15, color: 'var(--ink)' }}>{value}</div>
        <div className="small faint">{label}</div>
      </div>
    </div>
  );
}

// =====================================================================
// Areas
// =====================================================================

function AreasTab({ distributorUserId, isAdmin, onChanged }: {
  distributorUserId: string;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [areas, setAreas] = useState<DistributorArea[] | null>(null);
  const [allAreas, setAllAreas] = useState<Area[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [assignedRes, available] = await Promise.all([
        api.get<{ areas: DistributorArea[] }>(`/distributors/${distributorUserId}/areas`),
        api.get<Area[]>('/customers/routes'),
      ]);
      setAreas(assignedRes.areas);
      setAllAreas(available.filter((a) => a.status === 'active'));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load areas.');
      setAreas([]);
    }
  }, [distributorUserId, toast]);

  useEffect(() => { void load(); }, [load]);

  const assignedIds = new Set((areas ?? []).map((a) => a.routeId));
  const unassigned = (allAreas ?? []).filter((a) => !assignedIds.has(a.id));

  async function assign() {
    if (!selectedRouteId) return;
    setBusy(true);
    try {
      await api.post(`/distributors/${distributorUserId}/areas`, { routeId: selectedRouteId });
      toast.success('Area assigned.');
      setSelectedRouteId('');
      setAdding(false);
      void load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not assign area.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(routeId: string) {
    setBusy(true);
    try {
      await api.del(`/distributors/${distributorUserId}/areas/${routeId}`);
      toast.success('Area removed.');
      void load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove area.');
    } finally {
      setBusy(false);
    }
  }

  if (!areas) return <PageLoading />;

  return (
    <div className="col gap-12">
      {isAdmin ? (
        <div className="row gap-8">
          {adding ? (
            <>
              <Select
                value={selectedRouteId}
                onChange={(e) => setSelectedRouteId(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">Pick an area…</option>
                {unassigned.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}{a.city ? ` — ${a.city}` : ''}</option>
                ))}
              </Select>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || !selectedRouteId} onClick={() => void assign()}>
                Assign
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAdding(true)}>
              <Icon name="plus" size={14} /> Assign area
            </button>
          )}
        </div>
      ) : null}

      {areas.length === 0 ? (
        <EmptyState
          icon="map"
          title="No areas assigned"
          body="Assign areas so this distributor receives the secondary orders placed in them."
        />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Area</th>
                <th>City</th>
                <th>State</th>
                <th className="num">Retailers</th>
                {isAdmin ? <th aria-label="Actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {areas.map((a) => (
                <tr key={a.routeId}>
                  <td className="primary">{a.routeName}</td>
                  <td>{a.city ?? '—'}</td>
                  <td>{a.state ?? '—'}</td>
                  <td className="num">{a.customerCount}</td>
                  {isAdmin ? (
                    <td>
                      <button
                        type="button"
                        className="icon-btn danger"
                        title="Remove area"
                        disabled={busy}
                        onClick={() => void remove(a.routeId)}
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Retailers
// =====================================================================

interface CustomerOption {
  id: string;
  name: string;
  code: string | null;
  routeName: string | null;
}

function RetailersTab({ distributorUserId, isAdmin, onChanged }: {
  distributorUserId: string;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [retailers, setRetailers] = useState<DistributorCustomer[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [searchResults, setSearchResults] = useState<CustomerOption[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ customers: DistributorCustomer[]; nextCursor: string | null }>(`/distributors/${distributorUserId}/customers`);
      setRetailers(res.customers);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load retailers.');
      setRetailers([]);
    }
  }, [distributorUserId, toast]);

  useEffect(() => { void load(); }, [load]);

  // Load available customers when adding
  useEffect(() => {
    if (!adding) return;
    (async () => {
      try {
        const res = await api.get<{ customers: { id: string; name: string; code: string | null; routeName: string | null }[] }>(
          `/customers?limit=200`,
        );
        const assignedIds = new Set((retailers ?? []).map((r) => r.id));
        setSearchResults(res.customers.filter((c) => !assignedIds.has(c.id)));
      } catch {
        setSearchResults([]);
      }
    })();
  }, [adding, retailers]);

  async function assign() {
    if (!selectedCustomerId) return;
    setBusy(true);
    try {
      await api.put(`/distributors/customer/${selectedCustomerId}`, { distributorId: distributorUserId });
      toast.success('Customer assigned.');
      setSelectedCustomerId('');
      setAdding(false);
      void load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not assign customer.');
    } finally {
      setBusy(false);
    }
  }

  async function unassign(customerId: string) {
    setBusy(true);
    try {
      await api.put(`/distributors/customer/${customerId}`, { distributorId: null });
      toast.success('Customer unassigned.');
      void load();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not unassign customer.');
    } finally {
      setBusy(false);
    }
  }

  if (!retailers) return <PageLoading />;

  return (
    <div className="col gap-12">
      {isAdmin ? (
        <div className="row gap-8">
          {adding ? (
            <>
              <Select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">Pick a customer…</option>
                {searchResults.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.routeName ? ` — ${c.routeName}` : ''}{c.code ? ` (${c.code})` : ''}
                  </option>
                ))}
              </Select>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || !selectedCustomerId} onClick={() => void assign()}>
                Assign
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAdding(true)}>
              <Icon name="plus" size={14} /> Assign customer
            </button>
          )}
        </div>
      ) : null}

      {retailers.length === 0 ? (
        <EmptyState
          icon="users"
          title="No retailers assigned"
          body="Retailers are auto-assigned based on area, or can be manually assigned."
        />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Retailer</th>
                <th>Area</th>
                <th>Source</th>
                {isAdmin ? <th aria-label="Actions" /> : null}
              </tr>
            </thead>
            <tbody>
              {retailers.map((r) => (
                <tr key={r.id}>
                  <td className="primary">{r.name}</td>
                  <td>{r.routeName ?? '—'}</td>
                  <td>
                    <Badge tone={r.assignedVia === 'override' ? 'warning' : 'neutral'}>
                      {r.assignedVia === 'override' ? 'Override' : 'Area'}
                    </Badge>
                  </td>
                  {isAdmin ? (
                    <td>
                      <button
                        type="button"
                        className="icon-btn danger"
                        title="Unassign customer"
                        disabled={busy}
                        onClick={() => void unassign(r.id)}
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
